import { Divider } from '../../components/Divider';
import { PageHeading } from '../../components/PageHeading';
import { PanelCard } from '../../components/PanelCard';
import { SkeletonCard } from '../../components/SkeletonCard';
import { AllOptionsTimeslotPanel } from '../AllOptionsTimeslotPanel';
import { GuestDetailsPanel } from '../GuestDetailsPanel';
import { PreferencesPanel } from '../PreferencesPanel';
import { ReservationPanel } from '../ReservationPanel';
import { SingleOptionTimeslotPanel } from '../SingleOptionTimeslotPanel';
import { ScrollContainer, SkeletonContainer } from './SpaDaysBookingContent.styles';
import type { SpaDaysBookingContentProps } from './SpaDaysBookingContent.types';

export function SpaDaysBookingContent({
  title = 'Spa day.',
  subtitle = 'Tailor your visit, just the way you like it.',
  isLoading = false,
  guestCount,
  minGuests,
  maxGuests,
  onGuestCountChange,
  activeDate,
  selectedDate,
  availability,
  onDateSelect,
  onWeekNavigate,
  onCalendarClick,
  calendarLabels,
  guests,
  onExistingGuestSelect,
  onGuestChange,
  titleOptions,
  genderOptions,
  onAllOptionsTimeslotChange,
  onSingleOptionSelect,
  onSingleOptionTimeslotChange,
  preferencesEnabled,
  onPreferencesToggle,
  preferenceCategories,
  expandedPreferences,
  onPreferenceCategoryToggle,
  onPreferenceOptionToggle,
  onPreferenceOtherChange,
  onPreferenceFreeTextChange,
  labels,
}: SpaDaysBookingContentProps) {
  const selectedGuestIds = guests
    .map((g) => g.selectedGuestId)
    .filter((id): id is string => id !== undefined);
  return (
    <ScrollContainer>
      <PageHeading title={title} subtitle={subtitle} />

      <PanelCard>
        <ReservationPanel
          title={labels?.reservation?.title}
          subtitle={labels?.reservation?.subtitle}
          guestLabel={labels?.reservation?.guestLabel}
          guestCount={guestCount}
          minGuests={minGuests}
          maxGuests={maxGuests}
          onGuestCountChange={onGuestCountChange}
          activeDate={activeDate}
          selectedDate={selectedDate}
          availability={availability}
          onDateSelect={onDateSelect}
          onWeekNavigate={onWeekNavigate}
          onCalendarClick={onCalendarClick}
          calendarLabels={calendarLabels}
          counterDecreaseLabel={labels?.counter?.decrease}
          counterIncreaseLabel={labels?.counter?.increase}
        />
      </PanelCard>

      {isLoading ? (
        Array.from({ length: guestCount }, (_, i) => (
          <SkeletonContainer key={i}>
            <SkeletonCard variant="simple" />
            <SkeletonCard variant="simple" />
          </SkeletonContainer>
        ))
      ) : (
        <>
          {guests.map((guest, guestIndex) => {
            const disabledGuestIds = selectedGuestIds.filter((id) => id !== guest.selectedGuestId);

            return (
              <PanelCard key={guest.guestNumber}>
                <GuestDetailsPanel
                  guestNumber={guest.guestNumber}
                  existingGuests={guest.existingGuests}
                  selectedGuestId={guest.selectedGuestId}
                  disabledGuestIds={disabledGuestIds}
                  onExistingGuestSelect={(guestId) => onExistingGuestSelect(guestIndex, guestId)}
                  values={guest.values}
                  onChange={(field, value) => onGuestChange(guestIndex, field, value)}
                  titleOptions={titleOptions}
                  genderOptions={genderOptions}
                  errors={guest.errors}
                  labels={labels?.guestDetails}
                />

                {guest.optionsPanels.map((panel, panelIndex) => (
                  <div key={panelIndex}>
                    <Divider spacing="large" />
                    {panel.type === 'all' ? (
                      <AllOptionsTimeslotPanel
                        title={panel.title}
                        subtitle={panel.subtitle}
                        options={panel.options}
                        onTimeslotChange={(optionId, value) =>
                          onAllOptionsTimeslotChange(guestIndex, panelIndex, optionId, value)
                        }
                        noteText={panel.noteText}
                        noteIcon={panel.noteIcon}
                        timeslotPlaceholder={panel.timeslotPlaceholder}
                        timeslotAriaLabelFormatter={labels?.timeslotAriaLabelFormatter}
                      />
                    ) : (
                      <SingleOptionTimeslotPanel
                        title={panel.title}
                        subtitle={panel.subtitle}
                        options={panel.options}
                        selectedOptionId={panel.selectedOptionId}
                        selectedTimeslot={panel.selectedTimeslot}
                        onOptionSelect={(optionId) =>
                          onSingleOptionSelect(guestIndex, panelIndex, optionId)
                        }
                        onTimeslotChange={(value) =>
                          onSingleOptionTimeslotChange(guestIndex, panelIndex, value)
                        }
                        noteText={panel.noteText}
                        noteIcon={panel.noteIcon}
                        timeslotPlaceholder={panel.timeslotPlaceholder}
                        timeslotHeading={panel.timeslotHeading}
                        timeslotAriaLabelFormatter={labels?.timeslotAriaLabelFormatter}
                      />
                    )}
                  </div>
                ))}
              </PanelCard>
            );
          })}

          <PanelCard>
            <PreferencesPanel
              title={labels?.preferences?.title}
              enabled={preferencesEnabled}
              onToggle={onPreferencesToggle}
              categories={preferenceCategories}
              expandedCategories={expandedPreferences}
              onCategoryToggle={onPreferenceCategoryToggle}
              onOptionToggle={onPreferenceOptionToggle}
              onOtherChange={onPreferenceOtherChange}
              onFreeTextChange={onPreferenceFreeTextChange}
              otherButtonLabel={labels?.preferences?.otherButtonLabel}
            />
          </PanelCard>
        </>
      )}
    </ScrollContainer>
  );
}
